import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'

import { AppButton, PhotoInput, StickyHeaderNavigation } from '@/components'
import { BaseView, BaseText } from '@/components/base'
import { TextInputForm } from '@/components/form'
import { inputLimits } from '@/constants/inputLimits'
import { useSetupProfile, useGetMe, useUploadAvatar } from '@/domain'
import { useResetNavigation } from '@/hooks'
import { ICreateProfileSchema, useCreateProfileSchema } from '@/schemas'
import { useProfileDraftStore } from '@/store'
import { AddReferralCode } from '@/components/AddReferralCode'
import { ApiErrorCode } from '@/api/apiTypes'

export const SetupProfileForm = ({
  hasEmailAlreadyVerified = false,
}: {
  hasEmailAlreadyVerified?: boolean
}) => {
  const [isValidForm, setIsValidForm] = useState(true)
  const [showReferralInput, setShowReferralInput] = useState(false)

  const { t } = useTranslation()
  const resetTo = useResetNavigation()

  // Get the profile draft data from store
  const profileDraft = useProfileDraftStore((state) => state.draft)
  const updateProfileDraft = useProfileDraftStore((state) => state.updateDraft)

  // Get refetch function to update user data
  const { refetch: refetchUserData } = useGetMe()

  const schema = useCreateProfileSchema({
    isEmailRequired: !hasEmailAlreadyVerified,
  })

  const { mutateAsync: setupProfile, isPending: isCreateProfile } =
    useSetupProfile({
      onSuccess: async () => {
        // После успешного создания профиля обновляем данные пользователя
        try {
          await refetchUserData()
          // Затем переходим на главную
          resetTo('dashboard')
        } catch (error) {
          console.error('Failed to refetch user data:', error)
          resetTo('dashboard')
        }
      },
      onError: (error) => {
        if (error?.code === ApiErrorCode.USERNAME_ALREADY_EXISTS) {
          setTimeout(() => {
            setError('username', {
              type: 'manual',
              message:
                error.details?.detail?.description ||
                t('api:usernameAlreadyTaken'),
            })
          }, 0)
        } else if (error?.code === ApiErrorCode.EMAIL_ALREADY_EXISTS_ERROR) {
          setTimeout(() => {
            setError('email', {
              type: 'manual',
              message:
                error.details?.detail?.description ||
                t('api:emailAlreadyExists'),
            })
          }, 0)
        } else if (error.errorMessage) {
          toast.error(error.errorMessage)
        }
      },
    })

  // Hook for uploading avatar
  const { mutateAsync: uploadAvatar, isPending: isUploadingAvatar } =
    useUploadAvatar()

  const {
    control,
    handleSubmit,
    // watch,
    formState,
    setValue,
    setFocus,
    trigger,
    setError,
  } = useForm<ICreateProfileSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstName: profileDraft.firstName || '',
      lastName: profileDraft.lastName || '',
      username: profileDraft.username || '',
      email: hasEmailAlreadyVerified ? undefined : profileDraft.email || '',
      referralCode: profileDraft.referralCode || '',
      imageUri: profileDraft.avatar || '',
    },
    mode: 'onChange',
  })

  const isFormReadyToSubmit = formState.isDirty && isValidForm

  // Function for uploading profile photo
  const handlePhotoUpload = async (imageUrl: string) => {
    try {
      // Update draft with the local image URL immediately for preview
      updateProfileDraft({ avatar: imageUrl })

      // Convert blob URL to File object
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' })

      await uploadAvatar({ file })
    } catch (error) {
      console.error('Error uploading avatar:', error)
      toast.error('Failed to upload photo')
      // Revert the avatar in draft if upload fails
      updateProfileDraft({ avatar: '' })
    }
  }

  const handleSubmitForm = useCallback(
    async (formData: ICreateProfileSchema) => {
      const payload = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        username: formData.username.trim(),
        email: formData.email?.trim() || undefined,
        referral_code: formData.referralCode?.trim() || undefined,
      }

      try {
        await setupProfile(payload)
      } catch (error: any) {
        // Error is already handled in the mutation's onError callback
      }
    },
    [setupProfile],
  )

  useEffect(() => {
    setIsValidForm(formState.isValid)
  }, [formState.isValid])

  useEffect(() => {
    if (profileDraft.firstName) {
      trigger('firstName')
    }
    if (profileDraft.lastName) {
      trigger('lastName')
    }
    if (profileDraft.email && !hasEmailAlreadyVerified) {
      trigger('email')
    }
  }, [
    profileDraft.firstName,
    profileDraft.lastName,
    profileDraft.email,
    hasEmailAlreadyVerified,
    trigger,
  ])

  return (
    <BaseView gap={36} center className="flex-1 justify-center px-4">
      {/* Header */}
      <StickyHeaderNavigation showBackButton={false} />
      <BaseView gap={28}>
        <BaseText fontSize={32} className="font-semibold text-center">
          {t('screens:setupProfile.createProfile')}
        </BaseText>
      </BaseView>

      {/* Profile Form */}
      <BaseView gap={20} className="w-full max-w-md">
        {/* Profile Photo Section */}
        <BaseView center className="mb-6">
          <PhotoInput
            centralize
            size={144} // Smaller size for setup form
            sizeButtonIcon={40}
            iconSize={16}
            uri={profileDraft.avatar || ''}
            onSelectImage={handlePhotoUpload}
            isLoading={isUploadingAvatar}
          />
        </BaseView>

        <BaseView gap={12}>
          <TextInputForm
            control={control}
            name="firstName"
            label={`${t('form:fields.firstName')}*`}
            maxLength={inputLimits.firstName.max}
            onChangeText={(value) => {
              // Update profile draft as user types
              updateProfileDraft({ firstName: value })
              setValue('firstName', value, {
                shouldValidate: value.length >= 2,
                shouldDirty: true,
              })
            }}
            onBlur={() => trigger('firstName')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setFocus('lastName')
              }
            }}
          />

          <TextInputForm
            control={control}
            name="lastName"
            label={`${t('form:fields.lastName')}*`}
            maxLength={inputLimits.lastName.max}
            onChangeText={(value) => {
              // Update profile draft as user types
              updateProfileDraft({ lastName: value })
              setValue('lastName', value, {
                shouldValidate: value.length >= 2,
                shouldDirty: true,
              })
            }}
            onBlur={() => trigger('lastName')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setFocus('username')
              }
            }}
          />

          <TextInputForm
            control={control}
            name="username"
            label={`${t('form:fields.username')}*`}
            maxLength={inputLimits.username.max}
            onChangeText={(value) => {
              // Update profile draft as user types
              updateProfileDraft({ username: value })
              // Validate as user types if length is 4 or more, or if there are invalid characters
              setValue('username', value, {
                shouldValidate:
                  value.length >= 4 ||
                  /[@#+\p{Emoji_Presentation}\p{Extended_Pictographic}]/u.test(
                    value,
                  ),
                shouldDirty: true,
              })
            }}
            onBlur={() => trigger('username')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (showReferralInput) {
                  setFocus('referralCode')
                } else {
                  handleSubmit(handleSubmitForm)()
                }
              }
            }}
          />

          {!hasEmailAlreadyVerified && (
            <TextInputForm
              control={control}
              name="email"
              label="Email*"
              type="email"
              maxLength={inputLimits.email.max}
              onChangeText={(value) => {
                // Update profile draft as user types
                updateProfileDraft({ email: value })
                setValue('email', value, {
                  shouldValidate: value.includes('@'),
                  shouldDirty: true,
                })
              }}
              onBlur={() => trigger('email')}
            />
          )}

          {showReferralInput ? (
            <TextInputForm
              control={control}
              name="referralCode"
              label={t('form:fields.referralCode')}
              maxLength={inputLimits.referralCode.max}
              onBlur={() => trigger('referralCode')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(handleSubmitForm)()
                }
              }}
            />
          ) : (
            <AddReferralCode
              setShowReferralInput={setShowReferralInput}
              label={t('form:fields.referralCode')}
              className="self-center"
            />
          )}
        </BaseView>
      </BaseView>

      {/* Submit Button */}
      <BaseView className="w-full max-w-md">
        <AppButton
          label={t('form:actions.next')}
          disabled={!isFormReadyToSubmit}
          isLoading={isCreateProfile}
          onClick={handleSubmit(handleSubmitForm)}
          className="w-full"
        />
      </BaseView>
    </BaseView>
  )
}
